import { useTenant } from 'lib/hooks/useTenant';
import {
  AtiaAccreditedIcon,
  AussieServicedIcon,
  BestDealGuaranteeIcon,
  CoordinatorIcon,
  DEALSAWAY_BENEFIT_COLORS,
  PaperPlaneIcon,
  PaymentCardIcon,
} from './booking-benefit-icons';

const GWAT_BENEFITS = [
  {
    Icon: PaperPlaneIcon,
    labelClass: 'lg:max-w-[7.5rem]',
    text: 'Start your adventure today',
  },
  {
    Icon: CoordinatorIcon,
    labelClass: '',
    text: 'Meet your Trip Coordinator',
  },
  {
    Icon: PaymentCardIcon,
    labelClass: '',
    text: 'Spread the cost your way',
  },
] as const;

const DEALSAWAY_BENEFITS = [
  {
    Icon: AtiaAccreditedIcon,
    labelClass: 'max-w-[5rem]',
    text: 'ATIA Accredited',
  },
  {
    Icon: AussieServicedIcon,
    labelClass: 'max-w-[9rem]',
    text: 'Excellent Aussie Phone Service',
  },
  {
    Icon: BestDealGuaranteeIcon,
    labelClass: 'max-w-[7rem]',
    text: 'Best Deal Guarantee',
  },
] as const;

const BOOKING_BENEFITS_LAYOUT_CLASS = 'mx-4 mt-5 md:mb-5 md:mt-0 lg:mx-0';

const GwatBookingBenefits = () => (
  <section
    aria-label="Booking benefits"
    className={BOOKING_BENEFITS_LAYOUT_CLASS}
  >
    <ul
      aria-label="Booking benefits"
      className="grid grid-cols-3 overflow-hidden rounded-[1rem] bg-[#F0F9FC]"
      data-testid="gwat-booking-benefits"
    >
      {GWAT_BENEFITS.map(({ Icon, labelClass, text }, index) => (
        <li
          className={
            'relative flex min-w-0 flex-col items-center gap-2 px-[0.375rem] pb-3 pt-[0.875rem] text-center text-[#22343C] ' +
            'lg:flex-row lg:justify-center lg:gap-[0.875rem] lg:px-5 lg:py-[1.125rem] lg:text-left ' +
            (index > 0
              ? 'before:absolute before:bottom-[0.875rem] before:left-0 before:top-[0.875rem] before:w-px before:bg-[#D0E7EF] lg:before:bottom-4 lg:before:top-4'
              : '')
          }
          key={text}
        >
          <span
            aria-hidden="true"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#E4F4F9] text-teal-900 lg:h-12 lg:w-12"
            data-testid="benefit-icon-tile"
          >
            <Icon className="h-6 w-6 lg:h-[1.625rem] lg:w-[1.625rem]" />
          </span>
          <span
            className={
              'min-w-0 [text-wrap:balance] text-[0.8125rem] font-semibold leading-[1.25] lg:[text-wrap:wrap] lg:text-base lg:leading-[1.3] ' +
              labelClass
            }
          >
            {text}
          </span>
        </li>
      ))}
    </ul>
  </section>
);

const DealsAwayBookingBenefits = () => (
  <section
    aria-labelledby="dealsaway-benefits-heading"
    className={`${BOOKING_BENEFITS_LAYOUT_CLASS} rounded-[1rem] px-4 pb-4 pt-4`}
    data-testid="dealsaway-booking-benefits"
    style={{ backgroundColor: DEALSAWAY_BENEFIT_COLORS.surface }}
  >
    <h2
      className="text-center text-base font-semibold leading-[1.3]"
      id="dealsaway-benefits-heading"
      style={{ color: DEALSAWAY_BENEFIT_COLORS.label }}
    >
      Why book with DealsAway.com?
    </h2>
    <ul aria-label="DealsAway benefits" className="mt-3 grid grid-cols-3">
      {DEALSAWAY_BENEFITS.map(({ Icon, labelClass, text }, index) => (
        <li
          className="relative flex min-w-0 flex-col items-center gap-2 px-1 text-center lg:flex-row lg:justify-center lg:gap-[0.875rem] lg:px-4 lg:py-2 lg:text-left"
          key={text}
          style={{ color: DEALSAWAY_BENEFIT_COLORS.label }}
        >
          {index > 0 && (
            <span
              aria-hidden="true"
              className="absolute bottom-1 left-0 top-1 w-px lg:bottom-2 lg:top-2"
              data-testid="dealsaway-benefit-divider"
              style={{ backgroundColor: DEALSAWAY_BENEFIT_COLORS.divider }}
            />
          )}
          <span
            aria-hidden="true"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border bg-light-600 lg:h-12 lg:w-12"
            data-testid="benefit-icon-tile"
            style={{
              borderColor: DEALSAWAY_BENEFIT_COLORS.tileBorder,
              color: DEALSAWAY_BENEFIT_COLORS.icon,
            }}
          >
            <Icon className="h-6 w-6 lg:h-[1.625rem] lg:w-[1.625rem]" />
          </span>
          <span
            className={
              'min-w-0 [text-wrap:balance] text-[0.8125rem] font-semibold leading-[1.25] lg:text-base lg:leading-[1.3] ' +
              labelClass
            }
          >
            {text}
          </span>
        </li>
      ))}
    </ul>
  </section>
);

const BookingBenefits = () => {
  const { isDA, isGwat } = useTenant();

  if (isGwat) return <GwatBookingBenefits />;
  if (isDA) return <DealsAwayBookingBenefits />;

  return null;
};

export { BookingBenefits };
